import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import type { CardPreviewDensity } from "../card-previews";
import { CARD_PADDING } from "./frame-content-padding";

/**
 * The padded body of a card preview (mockup `.p`): padding in em, so it
 * grows with the wall's type. A regular card takes its content's height up
 * to `cap` (a `max-h-*` class in em); a compact card fills its container.
 */
export function PreviewBody({
  density,
  cap,
  className,
  children,
}: {
  density: CardPreviewDensity;
  cap?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex min-h-0 min-w-0 flex-col gap-[0.6em] overflow-hidden",
        density === "compact" ? "h-full p-[1.1em]" : cn(CARD_PADDING, cap),
        className,
      )}
      data-card-body=""
    >
      {children}
    </div>
  );
}

/** A card's own heading (mockup `.doc h3`): Satoshi, large, two lines. */
export function PreviewTitle({ children }: { children: ReactNode }) {
  return (
    <p
      className="mb-[0.6em] line-clamp-2 shrink-0 font-display text-[1.42em] font-medium leading-[1.16] tracking-[-0.025em] text-card-ink"
      data-testid="card-preview-title"
    >
      {children}
    </p>
  );
}

/** Live-data cards (task board, memory, connections): title and one line. */
export function PreviewSummary({
  frame,
  density,
  summary,
}: {
  frame: FrameData;
  density: CardPreviewDensity;
  summary: string;
}) {
  return (
    <PreviewBody density={density} cap="max-h-[11em]">
      <PreviewTitle>{frameDisplayTitle(frame)}</PreviewTitle>
      {summary && (
        <p
          className="line-clamp-2 leading-[1.55] text-card-body"
          data-testid="card-preview-summary"
        >
          {summary}
        </p>
      )}
    </PreviewBody>
  );
}

/** A checklist row's mark: ○ open, ● done. */
export function PreviewCheckMark({
  done,
  className,
}: {
  done: boolean;
  className?: string;
}) {
  return (
    // Centred on the first line: (1.45em line − 1.05em mark) / 2.
    <span
      aria-hidden="true"
      className={cn(
        "mt-[0.2em] h-[1.05em] w-[1.05em] shrink-0 rounded-full border-[1.5px]",
        done ? "border-transparent bg-card-muted" : "border-card-muted",
        className,
      )}
    />
  );
}

/**
 * A checklist row: ○ open, ● done and struck through, with its `detail`
 * on a muted line under it. A row with `onToggle` is the checkbox itself.
 */
export function PreviewCheckRow({
  done,
  detail,
  onToggle,
  children,
  "data-testid": testId,
}: {
  done: boolean;
  detail?: string;
  onToggle?: () => void;
  children: ReactNode;
  /** The toggle's test id. */
  "data-testid"?: string;
}) {
  const row = "flex items-start gap-[0.7em] text-left text-card-ink";
  const body = (
    <>
      <PreviewCheckMark done={done} />
      <span className="min-w-0">
        <span
          className={cn(
            "block truncate",
            done && "text-card-muted line-through",
          )}
        >
          {children}
        </span>
        {detail && (
          <span className="block truncate text-[0.84em] text-card-muted">
            {detail}
          </span>
        )}
      </span>
    </>
  );
  if (!onToggle) return <p className={row}>{body}</p>;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      className={cn(row, "w-full cursor-pointer")}
      onClick={onToggle}
      data-testid={testId}
    >
      {body}
    </button>
  );
}
